import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { isTranslation, Translatable } from '@app/translate/models/translatable';
import { ErrorDescription } from '@app/utils/error/error-description';
import { PortalError } from '@app/utils/error/portal-error';
import { sortBy } from '@app/utils/sort/sort-by';
import { StringMap } from '@app/utils/string-map';

interface ErrorGroup {
  errors: { description: ErrorDescription | null; id: string; title: Translatable }[];
  header: Translatable;
}

@Component({
  selector: 'portal-error-body',
  templateUrl: './error-body.component.html',
  styles: [],
  standalone: false,
})
export class ErrorBodyComponent implements OnChanges, OnInit {
  @Input()
  errors!: PortalError[];

  errorGroups: ErrorGroup[] = [];

  private errorGroupMap: StringMap<ErrorGroup> = {};

  constructor(private errorTransformerService: ErrorTransformerService) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('errors' in changes) {
      this.updateState(changes.errors.currentValue ?? []);
    }
  }

  ngOnInit() {
    this.updateState(this.errors ?? []);
  }

  /**
   * Set the iframe content by manually writing to the contentDocument of the
   * passed iframe. Normally we would use [srcdoc], but for some reason angular
   * omits the head part and therefore the content is not styled. There are some
   * workarounds but in our case they would not work (see
   * https://stackoverflow.com/q/55835362 for more information about this).
   */
  setIframeContent(iframe: HTMLIFrameElement, content: string): void {
    const document = iframe.contentDocument.open();
    document.write(content);
    document.close();
  }

  private updateState(errors: PortalError[]) {
    this.errorGroupMap = {};
    this.errorGroups = [];

    const flattened: PortalError[] = this.errorTransformerService.flattenErrors(errors);

    for (const error of flattened) {
      const description = this.errorTransformerService.describeError(error);
      const key = this.getI18nKey(error, description);

      if (this.errorGroupMap[key] == null) {
        this.errorGroupMap[key] = {
          errors: [],
          header: error.i18n ?? description?.title,
        };
      }

      this.errorGroupMap[key].errors.push({
        description,
        id: error.id,
        title: description?.title ?? error.i18n,
      });
    }

    this.errorGroups = Object.values(this.errorGroupMap).sort(sortBy('header'));
  }

  private getI18nKey(error: PortalError, description: ErrorDescription) {
    if (error.i18n != null) {
      if (isTranslation(error.i18n)) {
        return error.i18n.key;
      } else {
        return error.i18n;
      }
    } else {
      return description?.title;
    }
  }
}
